import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  MapPin,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import type { UpcomingGbpPostSnippet } from '../../hooks/useUpcomingGbpPostsSnippet';
import { useImageViewer } from '../ImageViewerProvider';
import { GoogleBusinessIcon } from '../PlatformIcon';
import ScheduledTimeEditor from '../marketing/ScheduledTimeEditor';

interface ControlCentreGbpPostPreviewModalProps {
  open: boolean;
  businessId: string;
  post: UpcomingGbpPostSnippet | null;
  onClose: () => void;
  onChanged: () => void;
}

function scheduleLabel(iso: string | null | undefined): string {
  if (!iso) return 'Not scheduled yet';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ControlCentreGbpPostPreviewModal({
  open,
  businessId,
  post,
  onClose,
  onChanged,
}: ControlCentreGbpPostPreviewModalProps) {
  const { open: openImageViewer } = useImageViewer();
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /** Which Google Business location this post belongs to (multi-location businesses). */
  const [locationName, setLocationName] = useState<string | null>(null);
  const [locationCount, setLocationCount] = useState(0);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !post) return;
    setContent(post.content || '');
    setImageUrl(post.image_url);
    setDirty(false);
    setScheduledFor(post.scheduled_for ?? null);
  }, [open, post]);

  useEffect(() => {
    if (!open || !post || !businessId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('google_business_connections')
        .select('id, location_name')
        .eq('business_id', businessId)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true);
      if (cancelled) return;
      const rows = data || [];
      setLocationCount(rows.length);
      // Older rows have no connection_id; with a single location that's unambiguous.
      const match = post.connection_id
        ? rows.find((r) => r.id === post.connection_id)
        : rows.length === 1
          ? rows[0]
          : null;
      setLocationName(match?.location_name || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, post, businessId]);

  if (!open || !post) return null;

  const busy = saving || rewriting || rejecting;
  const isAi = (post.source || 'ai') === 'ai';
  const isPublished = post.status === 'published';

  const handleDeletePublished = async () => {
    if (
      !confirm(
        'Delete this Google Business post? It will be removed from Google and from your calendar. This cannot be undone.',
      )
    ) {
      return;
    }
    setRejecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
        body: {
          action: 'delete_published',
          businessId,
          connectionId: post.connection_id,
          historyId: post.id,
        },
      });
      if (error || data?.error) {
        throw new Error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to delete post',
        );
      }
      toast.success('Post deleted');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete post');
    } finally {
      setRejecting(false);
    }
  };

  const invokeAction = async (
    action: 'update' | 'rewrite' | 'reject',
    body: Record<string, unknown> = {},
  ) => {
    const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
      body: {
        action,
        businessId,
        draftId: post.id,
        connectionId: post.connection_id,
        ...body,
      },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const handleSave = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error('Post content is required');
      return;
    }
    if (trimmed === (post.content || '').trim()) {
      toast.success('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await invokeAction('update', { content: trimmed });
      toast.success('Post updated');
      setDirty(false);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update post');
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async (nextIso: string) => {
    const data = await invokeAction('update', { scheduledFor: nextIso });
    setScheduledFor(String(data?.draft?.scheduled_for || nextIso));
    toast.success('Time updated');
    onChanged();
  };

  const handleRewrite = async () => {
    setRewriting(true);
    try {
      const data = await invokeAction('rewrite');
      const next = String(data?.draft?.content || '').trim();
      if (!next) throw new Error('AI rewrite returned empty content');
      setContent(next);
      if (data?.imageUrl) setImageUrl(data.imageUrl);
      setDirty(false);
      toast.success(
        data?.tokensUsed
          ? `AI rewrote the post · ${Number(data.tokensUsed).toLocaleString()} tokens`
          : 'AI rewrote the post',
      );
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI rewrite failed');
    } finally {
      setRewriting(false);
    }
  };

  const handleReject = async () => {
    if (
      !confirm(
        isAi
          ? 'Reject this AI post? It will be removed from the upcoming queue.'
          : 'Discard this staged post?',
      )
    ) {
      return;
    }
    setRejecting(true);
    try {
      await invokeAction('reject');
      toast.success(isAi ? 'Post rejected' : 'Post discarded');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject post');
    } finally {
      setRejecting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100200] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!busy) onClose();
        }}
        aria-hidden
      />
      <div
        role="dialog"
        aria-labelledby="gbp-post-preview-title"
        className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h2
              id="gbp-post-preview-title"
              className="text-base font-semibold text-gray-900"
            >
              Google Business post
            </h2>
            {!isPublished && scheduledFor ? (
              <ScheduledTimeEditor
                className="mt-0.5"
                scheduledFor={scheduledFor}
                disabled={busy}
                onSave={handleReschedule}
                suffix={isAi ? 'AI post' : 'Your post'}
              />
            ) : (
              <p className="text-[12px] text-gray-500 mt-0.5 truncate">
                {isPublished
                  ? `Published · ${scheduleLabel(post.created_at)}`
                  : scheduleLabel(post.scheduled_for)}
                {' · '}
                {isAi ? 'AI post' : 'Your post'}
              </p>
            )}
            <p className="mt-1.5 inline-flex items-center gap-1.5 max-w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-[12px] font-medium text-gray-800">
              <GoogleBusinessIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {locationName ||
                  (locationCount > 1 ? 'Location not recorded for this post' : 'Google Business location')}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {imageUrl ? (
            <button
              type="button"
              onClick={() =>
                openImageViewer({
                  images: [{ src: imageUrl, caption: 'Google Business post' }],
                  index: 0,
                })
              }
              className="block w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
            >
              <img
                src={imageUrl}
                alt=""
                className="w-full max-h-64 object-cover"
              />
            </button>
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-[12px] text-gray-500">
              <MapPin className="h-4 w-4 text-gray-400" />
              Text-only post — no image attached
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-500">Post content</label>
              {!isPublished && (
                <span className="text-[11px] text-gray-400">{content.length}/1500</span>
              )}
            </div>
            {isPublished ? (
              <p className="w-full text-sm rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 whitespace-pre-wrap text-gray-800">
                {content || '—'}
              </p>
            ) : (
              <textarea
                value={content}
                onChange={(e) => {
                  setContent(e.target.value.slice(0, 1500));
                  setDirty(true);
                }}
                rows={6}
                disabled={busy}
                className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
              />
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {isAi && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                <Sparkles className="h-3 w-3" />
                AI generated
              </span>
            )}
            {imageUrl && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                With image
              </span>
            )}
          </div>

          {isPublished ? (
            <p className="text-xs text-emerald-700 inline-flex items-center gap-1.5 font-medium">
              Published — this post is live on Google
            </p>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-gray-200 px-5 py-4 space-y-2">
          {isPublished ? (
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => void handleDeletePublished()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
              >
                {rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-3.5 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => void handleRewrite()}
                  disabled={busy}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-900 hover:bg-blue-100 disabled:opacity-50"
                >
                  {rewriting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  AI rewrite
                </button>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={busy || !dirty}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save changes
                </button>
              </div>
              <button
                type="button"
                onClick={() => void handleReject()}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-3 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {rejecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {isAi ? 'Reject post' : 'Discard post'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
